import { useSyncExternalStore } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { directoryKey } from "./use-directory";
import { uploadChatMedia, chatRequest, mergeMessages } from "./client";
import {
  chatKey,
  chatQueryOptions,
  historyWindow,
  CHAT_CACHE_MS,
} from "./chat-query";
import { useRealtimeStatus } from "./realtime-context";
export { chatKey } from "./chat-query";
import type {
  Attachment,
  Identity,
  Message,
  PendingMessage,
  SearchPage,
  Snapshot,
} from "../types";

const api = API_ENDPOINTS.webchat;
export function useChat(identity: Identity, chatId: string) {
  const client = useQueryClient();
  return useQuery(chatQueryOptions(client, identity, chatId));
}

type MessageAction =
  | {
      action: "edit";
      rootId: string;
      reqKey: string;
      text: string;
      expectedEventNo: string;
      mentions?: string[];
    }
  | {
      action: "delete";
      rootId: string;
      reqKey: string;
      expectedEventNo: string;
    }
  | { action: "reaction"; rootId: string; emoji: string; active: boolean };

export function useChatActions(identity: Identity, chatId: string) {
  const client = useQueryClient();
  const key = chatKey(identity, chatId);
  const invalidate = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: key }),
      client.invalidateQueries({ queryKey: directoryKey(identity) }),
    ]);
  };
  const applyMessage = async ({ message }: { message: Message }) => {
    await client.cancelQueries({ queryKey: key, exact: true });
    client.setQueryData<Snapshot>(key, (current) =>
      current ? historyWindow(current, [message]) : current,
    );
    await invalidate();
  };
  const send = useMutation({
    mutationFn: (item: PendingMessage) =>
      chatRequest<{ message: Message }>(api.messages(chatId), "POST", {
        kind: "TEXT",
        reqKey: item.reqKey,
        text: item.text,
        replyTo: item.replyTo,
        attachmentIds: item.attachmentIds,
        mentions: item.mentions ?? [],
      }),
    onSuccess: async ({ message }) => {
      // A history window drops roots newer than its last message, so a send
      // returns the transcript to latest; a failed load retries the same reqKey.
      if (!client.getQueryData<Snapshot>(key)?.historyWindow)
        return applyMessage({ message });
      const page = await chatRequest<Snapshot>(api.history(chatId));
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData<Snapshot>(
        key,
        historyWindow({ ...page, historyWindow: false }, [message]),
      );
      await invalidate();
    },
    retry: false,
    networkMode: "always",
  });
  const message = useMutation({
    mutationFn: ({ action, rootId, ...body }: MessageAction) =>
      chatRequest<{ message: Message }>(
        api.messageAction(chatId, rootId, action),
        "POST",
        body,
      ),
    onSuccess: applyMessage,
    onError: () => invalidate(),
    retry: false,
    networkMode: "always",
  });
  const preferences = useMutation({
    mutationKey: [...key, "preferences"],
    scope: { id: key.join(":") + ":preferences" },
    mutationFn: (body: { draft?: string; muted?: boolean }) =>
      chatRequest<{ draft: string; muted: boolean }>(
        api.preferences(chatId),
        "POST",
        body,
      ),
    onSuccess: async (prefs) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData<Snapshot>(key, (current) =>
        current ? { ...current, ...prefs } : current,
      );
      await client.invalidateQueries({ queryKey: directoryKey(identity) });
    },
    networkMode: "always",
  });
  const handoff = useMutation({
    mutationFn: (state: "REQUESTED" | "AI") =>
      chatRequest(api.handoff(chatId), "POST", { state }),
    onSuccess: invalidate,
    networkMode: "always",
  });
  const receipts = useMutation({
    mutationFn: (body: { lastGot: string; lastRead?: string }) =>
      chatRequest(api.receipts(chatId), "POST", body),
    onSuccess: invalidate,
    networkMode: "always",
  });
  const older = useMutation({
    mutationFn: (before: string) =>
      chatRequest<Snapshot>(`${api.history(chatId)}?before=${before}`),
    onSuccess: async (page) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData<Snapshot>(key, (current) =>
        current
          ? historyWindow(
              { ...current, before: page.before },
              page.messages,
              "older",
            )
          : current,
      );
    },
  });
  const latest = useMutation({
    mutationFn: () => chatRequest<Snapshot>(api.history(chatId)),
    onSuccess: async (page) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData(key, { ...page, historyWindow: false });
    },
  });
  return { send, message, preferences, handoff, receipts, older, latest };
}

export function useMessageSearch(
  identity: Identity,
  chatId: string,
  q: string,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: [...chatKey(identity, chatId), "search", q],
    enabled,
    gcTime: CHAT_CACHE_MS,
    maxPages: 8,
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      chatRequest<SearchPage>(
        `${api.search(chatId)}?${new URLSearchParams({ q, before: pageParam, limit: "50" })}`,
        "GET",
        undefined,
        signal,
      ),
    getNextPageParam: (page) =>
      page.hasMore && page.before ? page.before : undefined,
  });
}

export function useUpload(chatId: string) {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      onProgress,
    }: {
      file: File;
      signal: AbortSignal;
      onProgress: (percent: number | null) => void;
    }) => uploadChatMedia(api.attachments(chatId), file, signal, onProgress),
    networkMode: "always",
  });
}

export function useAttachment(chatId: string, attachId: string) {
  return useQuery({
    queryKey: ["webchat", "attachment", chatId, attachId],
    enabled: Boolean(attachId),
    queryFn: ({ signal }) =>
      chatRequest<{ attachment: Attachment }>(
        api.attachment(chatId, attachId),
        "GET",
        undefined,
        signal,
      ),
    refetchInterval: (query) =>
      ["CLEAR", "BLOCKED"].includes(query.state.data?.attachment.status ?? "")
        ? false
        : 1000,
    staleTime: 0,
  });
}

export function useTranscript(chatId: string) {
  return useMutation({
    mutationFn: async () => {
      let messages: Message[] = [];
      let before: string | null = null;
      const seen = new Set<string>();
      do {
        const page: Snapshot = await chatRequest<Snapshot>(
          `${api.history(chatId)}${before ? `?before=${before}` : ""}`,
        );
        messages = mergeMessages(messages, page.messages);
        before = page.before;
        if (before && seen.has(before))
          throw new Error("History did not advance. Please retry the export.");
        if (before) seen.add(before);
      } while (before);
      return messages;
    },
  });
}

const subscribeOnline = (callback: () => void) => {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
};
export const useOnline = () =>
  useSyncExternalStore(subscribeOnline, () => navigator.onLine);

/** Conversation access to the workspace's connection; never opens a socket. */
export function useLiveChat(
  _identity: Identity,
  chatId: string,
  enabled: boolean,
) {
  const realtime = useRealtimeStatus();
  return {
    connection: enabled ? realtime.connection : "Local UI fixture",
    typingMemberIds: realtime.chatId === chatId ? realtime.typingMemberIds : [],
    sendTyping: realtime.sendTyping,
  };
}
